import { setContentManagerData, setContentManagerDataDetails, setFileMetaData } from "../../../actions/contentManager";
import { storeDefaultTaskValues } from "../../../actions/task";
import { setLoader } from "../../../actions/taskSidebar";
import { editDataItem, searchTreeItem } from "../../../components/ContentManagementSystem/helper";
import { handleProjectDetailsUpdate } from "../../../components/ProjectSidebar/projectSidebar.common";
import { addOrganizationTag, getDefaultTaskValues, handleTaskKeyUpdate } from "../../../components/Tasks/tasks.service";
import store from "../../../store";
import { splitString } from "../../../utils";
import { updateContentAttachment } from "../../services/contentManger.services";
import { editWorkflow } from "../../services/workflow.services";

/**
 * appends new tag to the old ones
 * @param {*} tagId
 * @returns
 */
const appendNewTag = (tagId, tags) => {
  let oldTags = splitString(tags, ",");
  let newTagList = oldTags ? oldTags : [];
  newTagList.push(tagId);
  return newTagList;
};

/**
 * creates a payload for adding a new tag to the organization
 * @param {*} lastItem
 * @returns
 */
const newTagPayload = (lastItem, isTask) => {
  const state = store.getState();
  const { auth, taskSidebar } = state;
  return {
    tag: lastItem.label,
    companyId: auth.user.companyId,
    userId: auth.user.id,
    taskId: isTask ? taskSidebar.task.taskId : null,
  };
};

/**
 * appends tagId and tagColor to the dynamically created tag and updates tags accordingly
 */
const setTagsWithDynamicTag = (tagData, lastItem, projectTags, workflow) => {
  let newTagList = appendNewTag(tagData.tagId, projectTags);
  lastItem["value"] = tagData.tagId;
  lastItem["color"] = tagData.color;
  workflow && handleProjectDetailsUpdate("tags", newTagList.toString());
};

/**
 * add dynamic tags for organizations for tasks
 * @param {*} lastItem
 * @param {*} values
 * @param {*} projectId
 */
export const addDynamicTag = async (lastItem, values, projectId, setUpdate) => {
  const state = store.getState();
  const { taskSidebar } = state;
  values.pop();
  const tags = taskSidebar.isNewTask ? taskSidebar.newTaskData.tags : taskSidebar.task.tagIds;
  const sameItem = values.find((v) => v.value === lastItem.value);
  if (!sameItem) {
    values.push(lastItem);
    let payload = newTagPayload(lastItem, true);
    store.dispatch(setLoader("tags"));
    let tagData = await addOrganizationTag(payload);
    await store.dispatch(getDefaultTaskValues(payload.companyId, payload.userId, projectId, "tags"));
    let newTagList = appendNewTag(tagData.tagId, tags);
    store.dispatch(setLoader("tags"));
    setUpdate(false);
    handleTaskKeyUpdate(taskSidebar.isNewTask, "tags", newTagList.toString());
  }
};

/**
 * adds dynamic tags/new tags in case of workflow followers/tags
 */
export const addDynamicWorkflowTag = async (lastItem, values, tagIds, setTags, workflowId, isFollower, setIsUpdatingTags, setWorkflow, workflow) => {
  values.pop();
  const sameItem = values.find((v) => v.value === lastItem.value);
  const projectTags = tagIds && [...tagIds]?.map((d) => d.value).join(",");
  if (!sameItem) {
    values.push(lastItem);
    let payload = newTagPayload(lastItem, false);
    let tagData = await addOrganizationTag(payload);
    setTagsWithDynamicTag(tagData, lastItem, projectTags, true);

    if (setIsUpdatingTags) {
      setIsUpdatingTags(true);
    }

    if (workflowId) {
      let payload = { workflowId: workflowId };
      payload[isFollower ? "followers" : "tags"] = Array.prototype.map.call(values, (s) => s?.value).toString();
      const response = await store.dispatch(editWorkflow(payload));
      if (response) {
        setTags(values);

        if (setWorkflow && workflow) {
          const updatedWorkflow = { ...workflow };
          updatedWorkflow[isFollower ? "Followers" : "Tags"] = values.map((s) => s?.value).toString();
          setWorkflow(updatedWorkflow);
        }
      }
      if (setIsUpdatingTags) {
        setIsUpdatingTags(false);
      }
    } else {
      setTags(values);
      if (setIsUpdatingTags) {
        setIsUpdatingTags(false);
      }
    }
  }
};

/**updates cms data with respective tag updates
 * @author Muskan Thakur
 */
export const updateCmsAttachmentWithTags = async (selectedItem, tagIds) => {
  const state = store.getState();
  const { contentManagerData, fileMetaData } = state.contentManager;
  if (fileMetaData?.item) {
    store.dispatch(
      setFileMetaData({
        ...fileMetaData,
        item: {
          ...fileMetaData.item,
          tagIds: tagIds,
        },
      })
    );
  } else {
    const updatedData = editDataItem(contentManagerData, selectedItem, { tagIds });
    store.dispatch(setContentManagerDataDetails(searchTreeItem(updatedData, { path: selectedItem.path })));
    store.dispatch(setContentManagerData(updatedData));
  }

  await updateContentAttachment({
    attachmentId: selectedItem.id,
    tagIds,
    projectId: selectedItem?.projectId ?? null,
  });
};

/**
 * adds dynamic tags/new tags in case of cms
 */
export const addDynamicCmsTag = async (lastItem, values, tagIds, selectedItem) => {
  values.pop();
  const state = store.getState();
  const { defaultDetails } = state.tasks;
  const sameItem = values.find((v) => v.value === lastItem.value);
  const cmsTags = tagIds && [...tagIds]?.map((d) => d.value).join(",");
  if (!sameItem) {
    let payload = newTagPayload(lastItem, false);
    let tagData = await addOrganizationTag(payload);
    setTagsWithDynamicTag(tagData, lastItem, cmsTags, false);
    values.push(lastItem);
    const newTagList = [...defaultDetails?.tagList, lastItem];
    if (newTagList) {
      store.dispatch(storeDefaultTaskValues({ ...defaultDetails, tagList: [...newTagList] }));
    }
    await updateCmsAttachmentWithTags(selectedItem, values.map((s) => s?.value).toString());
  }
};

/**
 * used to filter selected tags from the tag list
 * @returns {Array}
 */
export const createDynamicTag = (tags, details) => {
  let tagIds = splitString(tags, ",");
  let taskTags = [];
  tagIds?.map((tagId) => {
    let tag = details?.tagList?.find((tag) => tag?.value?.toString() === tagId?.toString());
    if (tag) taskTags.push(tag);
  });
  return taskTags;
};
